import { ApiError, type ErrorCode, type ErrorResponseBody } from '@clockwise/shared';

export interface ClientOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

/** Pagination envelope every list endpoint returns alongside `data`. */
export interface PageMeta {
  total: number;
  limit: number;
  offset: number;
}

/** A paginated list response: `{ data, meta }`. */
export interface Paged<T> {
  data: T[];
  meta: PageMeta;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /**
   * Query parameters. An ARRAY value is repeated (`?key=a&key=b`) rather than
   * joined: a comma is a character a value may legitimately contain, and the
   * one domain that needed this — a report filter's member keys — has values
   * that are arbitrary words. Hono hands a repeated parameter to a validator
   * as an array, so the two sides already agree. `undefined` is omitted, and
   * an `undefined` inside an array is dropped rather than sent as "undefined".
   */
  query?: Record<string, string | number | boolean | undefined | readonly string[]>;
  signal?: AbortSignal;
  /** Extra headers — merged on top of the content-type default. Use for
   *  per-call concerns like `Idempotency-Key`. */
  headers?: Record<string, string>;
}

export class BaseClient {
  protected readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: ClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  }

  protected async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const url = new URL(this.baseUrl + path);
    appendQuery(url, opts.query);

    const headers: Record<string, string> = {};
    if (opts.body) headers['content-type'] = 'application/json';
    if (opts.headers) Object.assign(headers, opts.headers);

    const res = await this.fetchImpl(url.toString(), {
      method: opts.method ?? 'GET',
      credentials: 'include',
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });

    if (!res.ok) {
      let body: ErrorResponseBody | undefined;
      try {
        body = (await res.json()) as ErrorResponseBody;
      } catch {
        // body not JSON or absent — fall through
      }
      const code: ErrorCode = body?.error?.code ?? 'SYSTEM_ERROR';
      const message = body?.error?.message ?? `HTTP ${res.status}`;
      throw new ApiError({
        code,
        httpStatus: res.status,
        message,
        fields: body?.error?.fields,
        traceId: body?.error?.trace_id,
        retryAfter: body?.error?.retry_after,
      });
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  /**
   * Batch-fetch entities by id via `POST {path}` (the domain's `/find`
   * endpoint) with an `{ field: 'id', op: 'in' }` filter. Chunks ids into
   * groups of 100 (the server's IN-filter cap) and merges. Resolves exactly
   * the referenced ids — bounded by the caller's set, never the tenant — so
   * it can't silently drop rows the way a capped `list()` page does.
   * `meta.total` reflects the merged count. Shared by every entity client's
   * `findByIds` so the chunk size and merge logic live in one place.
   *
   * `extraFilters` are AND-ed onto every chunk's id-`in` filter (e.g. a
   * `status = 'active'` predicate) so callers can scope the batch fetch
   * server-side without a second round trip. The per-chunk `limit` stays at
   * the id count — an upper bound — so an extra filter only ever removes rows.
   */
  protected async findByIdsAt<T>(
    path: string,
    ids: readonly string[],
    extraFilters: readonly { field: string; op: string; value: unknown }[] = [],
  ): Promise<{ data: T[]; meta: { total: number; limit: number; offset: number } }> {
    if (ids.length === 0) return { data: [], meta: { total: 0, limit: 0, offset: 0 } };
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += 100) chunks.push(ids.slice(i, i + 100) as string[]);
    const results = await Promise.all(
      chunks.map((chunk) =>
        this.request<{ data: T[] }>(path, {
          method: 'POST',
          body: {
            filters: [{ field: 'id', op: 'in', value: chunk }, ...extraFilters],
            limit: chunk.length,
          },
        }),
      ),
    );
    const data = results.flatMap((r) => r.data);
    return { data, meta: { total: data.length, limit: ids.length, offset: 0 } };
  }

  /**
   * Fetch a binary resource. Returns the Blob plus the parsed filename
   * from `Content-Disposition` (when present) so the caller can trigger
   * a download with the server-suggested name.
   */
  protected async requestBlob(
    path: string,
    opts: {
      /** Defaults to `GET`. `POST` is for a download whose REQUEST is too big
       *  or too structured for a query string — a report spec is a nested
       *  object with arrays in it, and it is the same body `/query` takes. */
      method?: 'GET' | 'POST';
      body?: unknown;
      query?: Record<string, string | number | boolean | undefined | readonly string[]>;
      signal?: AbortSignal;
    } = {},
  ): Promise<{ blob: Blob; filename: string | null }> {
    const url = new URL(this.baseUrl + path);
    appendQuery(url, opts.query);

    const res = await this.fetchImpl(url.toString(), {
      method: opts.method ?? 'GET',
      credentials: 'include',
      headers: opts.body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });

    if (!res.ok) {
      let body: ErrorResponseBody | undefined;
      try {
        body = (await res.json()) as ErrorResponseBody;
      } catch {
        // body not JSON or absent
      }
      const code: ErrorCode = body?.error?.code ?? 'SYSTEM_ERROR';
      const message = body?.error?.message ?? `HTTP ${res.status}`;
      throw new ApiError({
        code,
        httpStatus: res.status,
        message,
        fields: body?.error?.fields,
        traceId: body?.error?.trace_id,
        retryAfter: body?.error?.retry_after,
      });
    }

    const blob = await res.blob();
    return { blob, filename: parseFilename(res.headers.get('content-disposition')) };
  }

  /**
   * Send a multipart/form-data POST. Browsers (and Bun's fetch) set the
   * boundary header automatically when `body` is a FormData, so we
   * intentionally do NOT set Content-Type here.
   */
  protected async requestMultipart<T>(
    path: string,
    formData: FormData,
    opts: { signal?: AbortSignal; headers?: Record<string, string> } = {},
  ): Promise<T> {
    const res = await this.fetchImpl(this.baseUrl + path, {
      method: 'POST',
      credentials: 'include',
      headers: opts.headers,
      body: formData,
      signal: opts.signal,
    });

    if (!res.ok) {
      let body: ErrorResponseBody | undefined;
      try {
        body = (await res.json()) as ErrorResponseBody;
      } catch {
        // body not JSON or absent
      }
      const code: ErrorCode = body?.error?.code ?? 'SYSTEM_ERROR';
      const message = body?.error?.message ?? `HTTP ${res.status}`;
      throw new ApiError({
        code,
        httpStatus: res.status,
        message,
        fields: body?.error?.fields,
        traceId: body?.error?.trace_id,
        retryAfter: body?.error?.retry_after,
      });
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
}

/**
 * Coerce a list/export filter object into the query-record shape that
 * `request()`/`requestBlob()` accept: drop `undefined` and empty-string values
 * (so the URL carries only set filters) and keep only string/number/boolean
 * scalars. Shared by every client's list + export calls.
 */
export function normalizeQuery(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') out[k] = v;
  }
  return out;
}

/**
 * Extract the filename from a Content-Disposition header. Accepts both
 * the legacy `filename="x"` form and RFC-5987 `filename*=UTF-8''x` form.
 * Returns null when no header is present or the form isn't recognized.
 */
function parseFilename(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // fall through to the quoted form
    }
  }
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1];
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare?.[1]) return bare[1].trim();
  return null;
}

/**
 * Query parameters onto a URL. See {@link RequestOptions.query}.
 *
 * ONE implementation for both `request` and `requestBlob`, because the two
 * disagreeing about how a repeated parameter is encoded is how an export comes
 * back filtered differently from the screen it was taken from.
 */
function appendQuery(
  url: URL,
  query?: Record<string, string | number | boolean | undefined | readonly string[]>,
): void {
  if (!query) return;
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined) continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item !== undefined) url.searchParams.append(k, String(item));
      }
      continue;
    }
    url.searchParams.set(k, String(v));
  }
}
